import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getResultDetail, saveCharacterResult } from '../../client';
import { useTranslation } from 'react-i18next';


const EnglishCharacterResult = ({ scoreData, navigate }) => {
    if (isEnglish) return <EnglishCharacterResult scoreData={scoreData} navigate={navigate} />;

    const { scores, groupA, groupB } = scoreData;
    const labels = {
        decision_style: 'Decision Style', emotional_response: 'Emotional Response',
        value_priority: 'Value Priority', consistency: 'Consistency',
        self_vs_other: 'Self vs. Others', reflection: 'Reflection',
        flexibility: 'Flexibility', action_initiation: 'Action Initiation',
        perception_gap: 'Perception Gap',
    };
    const describe = (value) => value >= 60 ? 'Strong' : value <= 40 ? 'Measured' : 'Balanced';
    return (
        <div className="result-container">
            <div className="content-wrapper">
                <section className="top-summary">
                    <div className="result-header">
                        <div className="badge">BEHAVIOR TENDENCY RESULT</div>
                        <h1 className="main-title">Context Analysis Report</h1>
                        <p className="sub-title">Your decision-making and relationship-interpretation patterns.</p>
                    </div>
                    <div className="summary-graphs">
                        <div className="graph-item"><div className="graph-label"><span>Action and Decisions</span><span className="score">{groupA}</span></div><p className="graph-desc">How quickly you decide, adapt, and begin acting.</p></div>
                        <div className="graph-item"><div className="graph-label"><span>Self-awareness and Relationships</span><span className="score">{groupB}</span></div><p className="graph-desc">How you reflect on yourself and interpret relationships.</p></div>
                    </div>
                </section>
                <section className="axis-explanations">
                    <div className="section-header"><h3>Indicator Details</h3><p>Scores describe tendencies, not fixed limits.</p></div>
                    <div className="explanation-grid">
                        {Object.entries(scores).map(([key, value]) => (
                            <div key={key} className="explanation-card">
                                <div className="exp-card-header"><span className="exp-title">{labels[key] || key}</span><span className="exp-score">{Math.round(value)}</span></div>
                                <p>{describe(value)} tendency on this dimension.</p>
                            </div>
                        ))}
                    </div>
                </section>
                <div className="actions"><button className="home-btn" onClick={() => navigate('/')}>Home</button><button className="retry-btn" onClick={() => { localStorage.removeItem('characterTestAnswers'); navigate('/character-test'); }}>Retake Test</button></div>
            </div>
        </div>
    );
};

const Result = () => {
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const isEnglish = (i18n.resolvedLanguage || i18n.language).startsWith('en');
    const { id } = useParams();
    const [scoreData, setScoreData] = useState(null);
    const [showDetails, setShowDetails] = useState(false);

    useEffect(() => {
        if (id) {
            const loadHistory = async () => {
                const data = await getResultDetail(id);
                if (data && data.payload) {
                    // Character payload is exactly the scoreData: { scores: normalizedScores, groupA, groupB }
                    setScoreData(data.payload);
                } else {
                    navigate('/mypage');
                }
            };
            loadHistory();
            return;
        }

        const savedAnswers = localStorage.getItem('characterTestAnswers');
        if (!savedAnswers) {
            navigate('/character-test');
            return;
        }

        const answers = JSON.parse(savedAnswers);

        // --- CALCULATION LOGIC ---
        const axes = [
            'decision_style', 'emotional_response', 'value_priority', 'consistency',
            'self_vs_other', 'reflection', 'flexibility', 'action_initiation', 'perception_gap'
        ];

        let rawScores = {};
        axes.forEach(axis => rawScores[axis] = 0);

        Object.values(answers).forEach(ans => {
            if (ans.effects) {
                Object.entries(ans.effects).forEach(([axis, value]) => {
                    if (rawScores[axis] !== undefined) {
                        rawScores[axis] += value;
                    }
                });
            }
        });

        const SCALE_FACTOR = 5;
        const normalizedScores = {};

        axes.forEach(axis => {
            let val = 50 + (rawScores[axis] * SCALE_FACTOR);
            val = Math.max(0, Math.min(100, val)); // Clamp 0-100
            normalizedScores[axis] = val;
        });

        const groupA = Math.round((
            normalizedScores.decision_style +
            normalizedScores.flexibility +
            normalizedScores.action_initiation
        ) / 3);

        const groupB = Math.round((
            normalizedScores.self_vs_other +
            normalizedScores.reflection +
            normalizedScores.perception_gap
        ) / 3);

        const finalResult = { scores: normalizedScores, groupA, groupB };
        setScoreData(finalResult);

        // Save result (only for new)
        const save = async () => {
            const labelMap = {
                decision_style: '🎯 결정 방식',
                emotional_response: '🌡️ 감정 반응',
                value_priority: '💎 가치 우선순위',
                consistency: '📈 지속성',
                self_vs_other: '👤 자기 기준',
                reflection: '🔍 성찰 깊이',
                flexibility: '🌊 유연성',
                action_initiation: '🏃 실행력',
                perception_gap: '🧩 인식 괴리'
            };

            const payload = {
                result_key: `Type ${Math.round(groupA)}-${Math.round(groupB)}`,
                scores: {
                    max: 100,
                    axes: Object.entries(normalizedScores).map(([key, val]) => ({
                        key,
                        label: labelMap[key] || key,
                        score: Math.round(val)
                    })),
                    extra: { groupA, groupB }
                },
                raw: { scores: normalizedScores, groupA, groupB },
                ...answers // Spread answers so Admin can see them
            };

            console.log("Saving Character result...", payload);
            await saveCharacterResult(payload);
            console.log("Character result saved.");
        };
        save();

    }, [navigate, id]);

    if (!scoreData) return <div className="loading">결과 분석 중...</div>;

    const { scores, groupA, groupB } = scoreData;

    // --- DYNAMIC TEXT GENERATORS ---
    const getGroupAText = () => {
        if (groupA >= 60) return "빠른 판단과 유연한 실행력이 특징이며, 환경 변화에 즉각적으로 반응합니다.";
        if (groupA <= 40) return "신중하게 상황을 분석한 뒤 움직이며, 확실한 구조가 잡혔을 때 행동합니다.";
        return "상황에 따라 속도를 조절하며, 균형 잡힌 의사결정을 내립니다.";
    };

    const getGroupBText = () => {
        if (groupB >= 60) return "자기 기준이 뚜렷하며, 내면의 성찰을 통해 타인과의 관계를 해석합니다.";
        if (groupB <= 40) return "타인의 감정과 반응을 중요하게 여기며, 조화로운 관계를 위해 노력합니다.";
        return "자신과 타인의 관점을 적절히 통합하여, 유연한 관계를 형성합니다.";
    };

    const getAnalysisA = () => {
        if (scores.decision_style > 60 && scores.action_initiation > 60) {
            return "준비가 끝났다고 느끼는 순간 빠르게 움직입니다. 통제 불가능한 변수가 발생해도 유연하게 대처하며, 목표를 수정해서라도 실행을 이어갑니다.";
        } else if (scores.decision_style <= 40 && scores.flexibility <= 40) {
            return "돌다리도 두들겨 보고 건너는 신중파입니다. 리스크를 최소화하는 것을 선호하며, 예측 가능한 범위 내에서 움직일 때 가장 효율적입니다.";
        } else {
            return "결단력은 있지만 신중함을 잃지 않습니다. 계획이 틀어지는 상황에서는 잠시 멈추고 상황을 재정비하려는 경향이 있습니다.";
        }
    };

    const getAnalysisB = () => {
        if (scores.self_vs_other > 60 && scores.reflection > 60) {
            return "관계 상황에서 자신의 선택을 반복적으로 해석하며, 이 과정이 깊어질수록 타인은 그 내면의 변화를 바로 알아채기 어려울 수 있습니다.";
        } else if (scores.self_vs_other <= 40 && scores.perception_gap <= 40) {
            return "타인의 의도를 깊이 생각하고 배려합니다. 있는 그대로의 관계를 유지하려고 노력하며, 오해를 만들지 않으려 합니다.";
        } else {
            return "확고한 자기 기준을 가지고 관계를 리드하지만, 때로는 상대의 반응을 과도하게 해석하여 스스로 피로감을 느낄 수도 있습니다.";
        }
    };

    const axisDescriptions = {
        decision_style: {
            title: "🎯 결정 방식",
            high: "빠른 결단: 정보가 완벽하지 않아도 직관과 경험을 믿고 속도감 있게 결정을 내립니다.",
            low: "신중한 판단: 발생 가능한 변수를 꼼꼼히 검토하며 리스크를 최소화하는 결정을 선호합니다."
        },
        emotional_response: {
            title: "🌡️ 감정 반응",
            high: "즉각적 반응: 자신의 감정 상태가 외부로 솔직하게 드러나며, 피드백이 빠르고 명확합니다.",
            low: "내면 처리: 감정을 안으로 갈무리하며, 평정심을 유지한 채 상황을 객관적으로 보려 합니다."
        },
        value_priority: {
            title: "💎 가치 우선순위",
            high: "결과 중심: 효율성과 목표 달성을 최우선으로 하며, 실질적인 성과에서 만족감을 얻습니다.",
            low: "과정·경험 중심: 목표뿐만 아니라 그 과정에서의 의미와 배움, 정서적 충족을 중요시합니다."
        },
        consistency: {
            title: "📈 일관성",
            high: "성향 고정적: 어떤 상황에서도 본인의 원칙과 태도를 일정하게 유지하려는 경향이 강합니다.",
            low: "상황 가변적: 환경이나 상대의 특성에 맞게 자신의 행동 방식을 유연하게 조정합니다."
        },
        self_vs_other: {
            title: "👤 자기 중심성",
            high: "자기 기준: 자신의 가치관과 필요를 명확히 인지하고, 이를 바탕으로 주체적인 선택을 합니다.",
            low: "타인 기준: 주변의 기대와 감정을 민감하게 살피며, 조화와 배려를 우선하여 의사결정을 합니다."
        },
        reflection: {
            title: "🔍 성찰 깊이",
            high: "성찰 높음: 자신의 행동과 감정의 원인을 깊이 있게 되돌아보며 내면의 성장을 도모합니다.",
            low: "성찰 낮음: 복잡한 자기 분석보다는 현재 상황의 해결과 다음 단계의 행동에 집중합니다."
        },
        flexibility: {
            title: "🌊 유연성",
            high: "상황 적응: 예기치 못한 변화를 자연스럽게 받아들이며 새로운 대안을 찾는 데 능숙합니다.",
            low: "계획 고수: 미리 정해진 질서와 체계를 신뢰하며, 일관된 흐름 속에서 안정감을 느낍니다."
        },
        action_initiation: {
            title: "🏃 실행력",
            high: "즉시 실행: 생각이 떠오르면 바로 몸으로 움직여 결과를 확인해보는 도전적 성향입니다.",
            low: "준비 후 실행: 충분한 준비와 계획이 갖춰진 상태에서 안정적으로 업무를 시작합니다."
        },
        perception_gap: {
            title: "🧩 인식 괴리",
            high: "인식 괴리 큼: 자신이 의도한 바와 타인이 받아들이는 지점의 차이를 독특한 관점으로 재해석합니다.",
            low: "인식 괴리 작음: 자신의 의도를 타인에게 명확히 전달하며, 상호 이해가 일치하는 것을 선호합니다."
        }
    };

    return (
        <div className="result-container">
            <div className="content-wrapper">
                {/* 1단: Top Summary */}
                <section className="top-summary fade-in">
                    <div className="result-header">
                        <div className="badge">행동경향 분석 결과</div>
                        <h1 className="main-title">Context Analysis Report</h1>
                        <p className="sub-title">당신의 의사결정과 관계해석 패턴입니다.</p>
                    </div>

                    <div className="summary-graphs">
                        <div className="graph-item">
                            <div className="graph-label">
                                <span>🚀 행동 개시 · 결정 방식</span>
                                <span className="score">{groupA}</span>
                            </div>
                            <div className="progress-bg">
                                <div className="progress-fill group-a" style={{ width: `${groupA}%` }}></div>
                            </div>
                            <p className="graph-desc">{getGroupAText()}</p>
                        </div>

                        <div className="graph-item">
                            <div className="graph-label">
                                <span>🧘 자기 인식 · 관계 해석</span>
                                <span className="score">{groupB}</span>
                            </div>
                            <div className="progress-bg">
                                <div className="progress-fill group-b" style={{ width: `${groupB}%` }}></div>
                            </div>
                            <p className="graph-desc">{getGroupBText()}</p>
                        </div>
                    </div>
                </section>

                {/* 2.5단: Axis Explanations */}
                <section className="axis-explanations fade-in-delay-1">
                    <div className="section-header">
                        <h3>📊 지표별 상세 설명</h3>
                        <p>각 점수가 의미하는 특성을 확인해보세요.</p>
                    </div>
                    <div className="explanation-grid">
                        {Object.entries(axisDescriptions).map(([key, desc]) => (
                            <div key={key} className="explanation-card">
                                <div className="exp-card-header">
                                    <span className="exp-title">{desc.title}</span>
                                    <span className={`exp-score ${scores[key] >= 50 ? 'high' : 'low'}`}>
                                        {Math.round(scores[key])}
                                    </span>
                                </div>
                                <div className="exp-content">
                                    <div className={`exp-item ${scores[key] >= 50 ? 'active' : ''}`}>
                                        <span className="dot high"></span>
                                        <p>{desc.high}</p>
                                    </div>
                                    <div className={`exp-item ${scores[key] < 50 ? 'active' : ''}`}>
                                        <span className="dot low"></span>
                                        <p>{desc.low}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* 3단: Group Analysis Cards */}
                <section className="analysis-cards fade-in-delay-1">
                    <div className="card">
                        <div className="card-header">
                            <h3>⚡ 결정과 실행의 패턴</h3>
                            <div className="chips">
                                <span>결정 {scores.decision_style}</span>
                                <span>유연성 {scores.flexibility}</span>
                                <span>행동 {scores.action_initiation}</span>
                            </div>
                        </div>
                        <div className="card-body">
                            <p>{getAnalysisA()}</p>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header">
                            <h3>🔍 관계와 내면의 해석</h3>
                            <div className="chips">
                                <span>자기기준 {scores.self_vs_other}</span>
                                <span>성찰 {scores.reflection}</span>
                                <span>괴리 {scores.perception_gap}</span>
                            </div>
                        </div>
                        <div className="card-body">
                            <p>{getAnalysisB()}</p>
                        </div>
                    </div>
                </section>

                {/* 3단: Individual Axis Details */}
                <section className="details-section fade-in-delay-2">
                    <div className="details-header">
                        <h3>📊 상세 지표 분석</h3>
                    </div>

                    <div className="axis-list">
                        <div className="axis-item">
                            <label>감정 반응</label>
                            <div className="bar-container">
                                <div className="bar" style={{ width: `${scores.emotional_response}%` }}></div>
                            </div>
                            <span className="value">{scores.emotional_response}</span>
                        </div>
                        <div className="axis-item">
                            <label>가치 우선순위</label>
                            <div className="bar-container">
                                <div className="bar" style={{ width: `${scores.value_priority}%` }}></div>
                            </div>
                            <span className="value">{scores.value_priority}</span>
                        </div>
                        <div className="axis-item">
                            <label>일관성</label>
                            <div className="bar-container">
                                <div className="bar" style={{ width: `${scores.consistency}%` }}></div>
                            </div>
                            <span className="value">{scores.consistency}</span>
                        </div>
                    </div>

                    {showDetails && (
                        <div className="axis-list more-details">
                            <div className="axis-item">
                                <label>결정 방식</label>
                                <div className="bar-container"><div className="bar sub" style={{ width: `${scores.decision_style}%` }}></div></div>
                                <span className="value">{scores.decision_style}</span>
                            </div>
                            <div className="axis-item">
                                <label>유연성</label>
                                <div className="bar-container"><div className="bar sub" style={{ width: `${scores.flexibility}%` }}></div></div>
                                <span className="value">{scores.flexibility}</span>
                            </div>
                            <div className="axis-item">
                                <label>행동 개시</label>
                                <div className="bar-container"><div className="bar sub" style={{ width: `${scores.action_initiation}%` }}></div></div>
                                <span className="value">{scores.action_initiation}</span>
                            </div>
                            <div className="axis-item">
                                <label>자기 중심성</label>
                                <div className="bar-container"><div className="bar sub" style={{ width: `${scores.self_vs_other}%` }}></div></div>
                                <span className="value">{scores.self_vs_other}</span>
                            </div>
                            <div className="axis-item">
                                <label>성찰 깊이</label>
                                <div className="bar-container"><div className="bar sub" style={{ width: `${scores.reflection}%` }}></div></div>
                                <span className="value">{scores.reflection}</span>
                            </div>
                            <div className="axis-item">
                                <label>인식 괴리</label>
                                <div className="bar-container"><div className="bar sub" style={{ width: `${scores.perception_gap}%` }}></div></div>
                                <span className="value">{scores.perception_gap}</span>
                            </div>
                        </div>
                    )}

                    <button className="toggle-btn" onClick={() => setShowDetails(!showDetails)}>
                        {showDetails ? "상세 분석 접기 ▲" : "모든 지표 보기 ▼"}
                    </button>
                </section>

                <div className="main-actions">
                    <button className="home-btn" onClick={() => navigate('/')}>
                        메인으로 돌아가기
                    </button>
                    <button className="restart-btn" onClick={() => {
                        localStorage.removeItem('characterTestAnswers');
                        navigate('/character-test');
                    }}>
                        다시 테스트하기
                    </button>
                </div>
            </div>

            <style jsx>{`
                .result-container {
                    min-height: 100vh;
                    padding: 4rem 1.5rem;
                    background: #0f172a;
                    color: #fff;
                    display: flex;
                    justify-content: center;
                }
                
                .loading {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 1.2rem;
                    background: #0f172a;
                }

                .content-wrapper {
                    max-width: 600px;
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    gap: 3rem;
                }

                /* Animation */
                .fade-in { animation: fadeIn 0.8s ease-out forwards; opacity: 0; }
                .fade-in-delay-1 { animation: fadeIn 0.8s ease-out 0.3s forwards; opacity: 0; }
                .fade-in-delay-2 { animation: fadeIn 0.8s ease-out 0.6s forwards; opacity: 0; }

                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                /* Section 1: Top Summary */
                .result-header {
                    text-align: center;
                    margin-bottom: 3rem;
                }

                .badge {
                    display: inline-block;
                    background: rgba(139, 92, 246, 0.2);
                    color: #a78bfa;
                    padding: 0.5rem 1rem;
                    border-radius: 100px;
                    font-size: 0.85rem;
                    font-weight: 600;
                    margin-bottom: 1rem;
                }

                .main-title {
                    font-size: 2.2rem;
                    font-weight: 800;
                    margin-bottom: 0.5rem;
                    background: linear-gradient(to right, #ffffff, #cbd5e1);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                .sub-title {
                    color: #94a3b8;
                    font-size: 1.05rem;
                }

                .summary-graphs {
                    display: flex;
                    flex-direction: column;
                    gap: 2rem;
                    padding: 2rem;
                    background: rgba(255,255,255,0.03);
                    border-radius: 24px;
                    border: 1px solid rgba(255,255,255,0.05);
                }

                .graph-item {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                }

                .graph-label {
                    display: flex;
                    justify-content: space-between;
                    font-weight: 600;
                    color: #e2e8f0;
                    margin-bottom: 0.5rem;
                }

                .score {
                    color: #3b82f6;
                    font-weight: bold;
                }

                .progress-bg {
                    width: 100%;
                    height: 12px;
                    background: rgba(255,255,255,0.1);
                    border-radius: 10px;
                    overflow: hidden;
                }

                .progress-fill {
                    height: 100%;
                    border-radius: 10px;
                }

                .group-a { background: linear-gradient(90deg, #3b82f6, #60a5fa); }
                .group-b { background: linear-gradient(90deg, #8b5cf6, #a78bfa); }

                .graph-desc {
                    margin-top: 0.8rem;
                    font-size: 0.95rem;
                    color: #cbd5e1;
                    line-height: 1.5;
                }

                /* Section 2: Analysis Cards */
                .analysis-cards {
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem;
                }

                .card {
                    background: rgba(30, 41, 59, 0.4);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 20px;
                    padding: 2rem;
                }

                .card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.2rem;
                    flex-wrap: wrap;
                    gap: 1rem;
                }

                .card-header h3 {
                    font-size: 1.15rem;
                    font-weight: 700;
                    color: #fff;
                    margin: 0;
                }

                .chips {
                    display: flex;
                    gap: 0.5rem;
                    flex-wrap: wrap;
                }

                .chips span {
                    font-size: 0.75rem;
                    padding: 0.3rem 0.6rem;
                    background: rgba(255, 255, 255, 0.08);
                    border-radius: 6px;
                    color: #94a3b8;
                }

                .card-body p {
                    color: #cbd5e1;
                    line-height: 1.7;
                    margin: 0;
                    font-size: 0.95rem;
                }

                /* Section 3: Details */
                .details-section {
                    background: rgba(0, 0, 0, 0.2);
                    border-radius: 24px;
                    padding: 2rem;
                }

                .details-header h3 {
                    margin-bottom: 1.5rem;
                    font-size: 1.1rem;
                    color: #adbac7;
                }

                .axis-list {
                    display: flex;
                    flex-direction: column;
                    gap: 1.2rem;
                }

                .axis-item {
                    display: grid;
                    grid-template-columns: 120px 1fr 40px;
                    align-items: center;
                    gap: 1rem;
                }

                .axis-item label {
                    font-size: 0.9rem;
                    color: #94a3b8;
                    text-align: right;
                }
                
                .axis-item .value {
                     font-size: 0.85rem;
                     color: #64748b;
                     text-align: right;
                }

                .bar-container {
                    height: 8px;
                    background: rgba(255, 255, 255, 0.05);
                    border-radius: 4px;
                    overflow: hidden;
                    width: 100%;
                }

                .bar {
                    height: 100%;
                    background: #475569;
                    border-radius: 4px;
                }
                
                .bar.sub {
                    background: #334155;
                }

                .more-details {
                    margin-top: 2rem;
                    padding-top: 2rem;
                    border-top: 1px solid rgba(255, 255, 255, 0.1);
                }

                .toggle-btn {
                    width: 100%;
                    margin-top: 2rem;
                    padding: 1rem;
                    background: transparent;
                    border: 1px dashed rgba(255, 255, 255, 0.15);
                    color: #94a3b8;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 0.9rem;
                }

                .toggle-btn:hover {
                    color: #fff;
                    border-color: rgba(255, 255, 255, 0.3);
                    background: rgba(255, 255, 255, 0.03);
                }

                .main-actions {
                    display: flex;
                    gap: 1rem;
                    margin-top: 1rem;
                }

                .home-btn, .restart-btn {
                    flex: 1;
                    padding: 1.2rem;
                    border-radius: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                    font-size: 1rem;
                }

                .home-btn {
                    background: transparent;
                    color: #94a3b8;
                    border: 1px solid rgba(255,255,255,0.1);
                }

                .home-btn:hover {
                    background: rgba(255,255,255,0.05);
                    color: white;
                }

                .restart-btn {
                    background: #3b82f6;
                    color: white;
                    border: none;
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
                }

                .restart-btn:hover {
                    background: #2563eb;
                    transform: translateY(-2px);
                }

                @media (max-width: 640px) {
                    .axis-item {
                        grid-template-columns: 1fr;
                        gap: 0.5rem;
                    }
                    .axis-item label {
                        text-align: left;
                    }
                    .axis-item .value {
                        display: none;
                    }
                    .main-actions {
                        flex-direction: column-reverse;
                    }
                }

                /* New Axis Explanations Styles */
                .axis-explanations {
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem;
                }

                .section-header {
                    margin-bottom: 0.5rem;
                }

                .section-header h3 {
                    font-size: 1.2rem;
                    color: #fff;
                    margin-bottom: 0.3rem;
                }

                .section-header p {
                    font-size: 0.9rem;
                    color: #94a3b8;
                }

                .explanation-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 1rem;
                }

                .explanation-card {
                    background: rgba(30, 41, 59, 0.4);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 16px;
                    padding: 1.5rem;
                }

                .exp-card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1rem;
                    padding-bottom: 0.8rem;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
                }

                .exp-title {
                    font-weight: 700;
                    color: #e2e8f0;
                    font-size: 1rem;
                }

                .exp-score {
                    font-size: 0.9rem;
                    font-weight: 800;
                    padding: 0.2rem 0.6rem;
                    border-radius: 8px;
                }

                .exp-score.high { background: rgba(59, 130, 246, 0.2); color: #60a5fa; }
                .exp-score.low { background: rgba(139, 92, 246, 0.2); color: #a78bfa; }

                .exp-content {
                    display: flex;
                    flex-direction: column;
                    gap: 0.8rem;
                }

                .exp-item {
                    display: flex;
                    gap: 0.8rem;
                    opacity: 0.4;
                    filter: grayscale(1);
                    transition: all 0.3s;
                }

                .exp-item.active {
                    opacity: 1;
                    filter: grayscale(0);
                }

                .dot {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    margin-top: 0.5rem;
                    flex-shrink: 0;
                }

                .dot.high { background: #3b82f6; box-shadow: 0 0 8px rgba(59, 130, 246, 0.5); }
                .dot.low { background: #8b5cf6; box-shadow: 0 0 8px rgba(139, 92, 246, 0.5); }

                .exp-item p {
                    margin: 0;
                    font-size: 0.9rem;
                    line-height: 1.5;
                    color: #cbd5e1;
                }

                .exp-item.active p {
                    color: #fff;
                    font-weight: 500;
                }
            `}</style>
        </div>
    );
};

export default Result;
